import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import {
  isPushSupported,
  getNotificationPermission,
  enableDailyReminders,
  disableDailyReminders
} from '../../utils/NotificationManager';

const AccountSettings = () => {
  const navigate = useNavigate();
  const { user, logout, isPremium, isTrialing, refreshUser, token } = useAuth();
  const { language, t, isSpanish } = useLanguage();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Profile state
  const [name, setName] = useState(user?.name || '');
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Password visibility toggles
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  // Notification state
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  
  // Portal state
  const [portalLoading, setPortalLoading] = useState(false);
  
  // Email preferences state
  const [dailyEmailEnabled, setDailyEmailEnabled] = useState(true);
  const [emailLanguage, setEmailLanguage] = useState('en');
  const [emailPrefsLoading, setEmailPrefsLoading] = useState(false);

  // Handle Manage Subscription (Stripe Portal)
  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const data = await api.createPortalSession();
      if (data.portal_url) {
        window.location.href = data.portal_url;
      }
    } catch (error) {
      console.error('Error opening subscription portal:', error);
      setMessage({
        type: 'error',
        text: isSpanish 
          ? 'No se puede abrir la gestión de suscripción. Intenta de nuevo.'
          : 'Unable to open subscription management. Please try again.'
      });
    } finally {
      setPortalLoading(false);
    }
  };

  // Handle Upgrade Now - SELF-HEALING BILLING BRIDGE
  // Trial users: Checkout with card pre-filled + coupon field
  // Others: Portal or fresh checkout
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  
  const handleUpgradeNow = async () => {
    setUpgradeLoading(true);
    try {
      const result = await api.smartUpgrade(language);
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Error with upgrade:', error);
      setMessage({
        type: 'error',
        text: isSpanish 
          ? 'No se puede procesar. Intenta de nuevo.'
          : 'Unable to process. Please try again.'
      });
    } finally {
      setUpgradeLoading(false);
    }
  };


  // Check notification status on mount
  useEffect(() => {
    setPushSupported(isPushSupported());
    
    // Fetch notification status from server
    const fetchNotificationStatus = async () => {
      try {
        const response = await api.getNotificationStatus();
        setNotificationsEnabled(response.enabled && response.has_subscription);
      } catch (error) {
        console.error('Failed to fetch notification status:', error);
      }
    };

    if (token) {
      fetchNotificationStatus();
    }
  }, [token]);

  // Fetch email preferences on mount
  useEffect(() => {
    const fetchEmailPrefs = async () => {
      try {
        const response = await api.getEmailPreferences();
        setDailyEmailEnabled(response.enabled);
        setEmailLanguage(response.language || 'en');
      } catch (error) {
        console.error('Failed to fetch email preferences:', error);
      }
    };
    if (token) {
      fetchEmailPrefs();
    }
  }, [token]);

  // Handle email preferences update
  const handleEmailPrefsUpdate = async (enabled, lang) => {
    setEmailPrefsLoading(true);
    try {
      await api.updateEmailPreferences(enabled, lang);
      setDailyEmailEnabled(enabled);
      setEmailLanguage(lang);
      setMessage({ type: 'success', text: isSpanish ? 'Preferencias actualizadas' : 'Preferences updated' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update preferences' });
    } finally {
      setEmailPrefsLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: t('profile'), icon: 'User' },
    { id: 'security', label: t('security'), icon: 'Lock' },
    { id: 'subscription', label: t('subscription'), icon: 'CreditCard' },
    { id: 'data', label: t('dataPrivacy'), icon: 'Shield' },
  ];

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await api.updateProfile(name);
      await refreshUser();
      setMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters with a number' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await api.updatePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMessage({ type: 'success', text: 'Password updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Handle Daily Reminders toggle
  const handleNotificationToggle = async () => {
    if (!pushSupported) {
      setMessage({ type: 'error', text: 'Push notifications are not supported in this browser' });
      return;
    }

    setNotificationLoading(true);
    setMessage({ type: '', text: '' });

    try {
      if (notificationsEnabled) {
        // Disable notifications
        const result = await disableDailyReminders(api);
        if (result.success) {
          setNotificationsEnabled(false);
          setMessage({ type: 'success', text: result.message });
        } else {
          setMessage({ type: 'error', text: result.message });
        }
      } else {
        // Enable notifications
        const result = await enableDailyReminders(api);
        if (result.success) {
          setNotificationsEnabled(true);
          setMessage({ type: 'success', text: result.message });
        } else {
          setMessage({ type: 'error', text: result.message });
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update notification settings' });
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      await api.deleteAccount();
      logout();
      navigate('/login');
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      setLoading(false);
    }
  };

  const renderProfileTab = () => (
    <div className="space-y-6">
      <form onSubmit={handleUpdateProfile} className="bento-item">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="User" size={20} color="#FFD700" />
          {t('personalInfo')}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              {t('fullName')}
            </label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              {t('emailAddress')}
            </label>
            <Input
              type="email"
              value={user?.email}
              disabled
              className="bg-muted cursor-not-allowed"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('emailCannotBeChanged')}
            </p>
          </div>
          <Button type="submit" disabled={loading}>
            {loading ? t('saving') : t('saveChanges')}
          </Button>
        </div>
      </form>

      {/* Email Correspondence Language */}
      <div className="bento-item">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Mail" size={20} color="#FFD700" />
          {isSpanish ? 'Idioma de Correspondencia' : 'Correspondence Language'}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {isSpanish 
            ? 'Esto controla como recibiras tus correos de LUMIVETA - en ingles o espanol.'
            : 'This controls how you receive your emails from LUMIVETA - in English or Spanish.'}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => handleEmailPrefsUpdate(dailyEmailEnabled, 'en')}
            disabled={emailPrefsLoading}
            data-testid="email-lang-english"
            className={`flex-1 py-3 px-4 rounded-lg border transition-colors font-medium ${
              emailLanguage === 'en' 
                ? 'bg-primary text-primary-foreground border-primary' 
                : 'border-border text-muted-foreground hover:border-primary/50'
            }`}
          >
            English
          </button>
          <button
            type="button"
            onClick={() => handleEmailPrefsUpdate(dailyEmailEnabled, 'es')}
            disabled={emailPrefsLoading}
            data-testid="email-lang-spanish"
            className={`flex-1 py-3 px-4 rounded-lg border transition-colors font-medium ${
              emailLanguage === 'es' 
                ? 'bg-primary text-primary-foreground border-primary' 
                : 'border-border text-muted-foreground hover:border-primary/50'
            }`}
          >
            Espanol
          </button>
        </div>
        {emailPrefsLoading && (
          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
            <Icon name="Loader2" size={14} className="animate-spin" />
            {isSpanish ? 'Actualizando...' : 'Updating...'}
          </p>
        )}
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <form onSubmit={handleUpdatePassword} className="bento-item">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Lock" size={20} color="#FFD700" />
          {t('changePassword')}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              {t('currentPassword')}
            </label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-input pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                tabIndex={-1}
              >
                <Icon name={showCurrentPassword ? "EyeOff" : "Eye"} size={18} />
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              {t('newPassword')}
            </label>
            <div className="relative">
              <Input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-input pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                tabIndex={-1}
              >
                <Icon name={showNewPassword ? "EyeOff" : "Eye"} size={18} />
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              {t('confirmNewPassword')}
            </label>
            <div className="relative">
              <Input
                type={showConfirmNewPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-input pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                tabIndex={-1}
              >
                <Icon name={showConfirmNewPassword ? "EyeOff" : "Eye"} size={18} />
              </button>
            </div>
          </div>
          <Button type="submit" disabled={loading}>
            {loading ? t('updating') : t('updatePassword')}
          </Button>
        </div>
      </form>
    </div>
  );

  const renderSubscriptionTab = () => {
    const usage = user?.usage || { used: 0, limit: 5, remaining: 5, limit_type: 'daily' };
    
    return (
      <div className="space-y-6">
        <div className="bento-item">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name={isPremium ? 'Crown' : isTrialing ? 'Gift' : 'Sparkles'} size={20} color="#FFD700" />
            {isSpanish ? 'Plan Actual' : 'Current Plan'}
          </h3>
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg mb-4">
            <div>
              <p className="font-semibold text-foreground">
                {isPremium ? t('premiumMember') : isTrialing ? t('freeTrialAccount') : (isSpanish ? 'Gratis' : 'Free')}
              </p>
              <p className="text-sm text-muted-foreground">
                {isPremium 
                  ? (isSpanish ? '7 reflexiones diarias' : '7 daily reflections')
                  : isTrialing 
                    ? (isSpanish ? '7 reflexiones diarias' : '7 daily reflections')
                    : t('subscribeToAccess')}
              </p>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              isPremium ? 'bg-primary/20 text-primary' : isTrialing ? 'bg-amber-500/20 text-amber-400' : 'bg-muted text-muted-foreground'
            }`}>
              {isPremium ? (isSpanish ? 'Activo' : 'Active') : isTrialing ? (isSpanish ? 'Prueba' : 'Trial') : (isSpanish ? 'Inactivo' : 'Inactive')}
            </div>
          </div>
          
          {/* Usage Display */}
          {(isPremium || isTrialing) && (
            <div className="p-4 bg-muted/50 rounded-lg mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">
                  {t('dailyReflectionsUsed')}
                </span>
                <span className="text-sm text-muted-foreground">
                  {usage.used} / {usage.limit}
                </span>
              </div>
              <div className="h-2 bg-background rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    usage.remaining <= 1 ? 'bg-amber-500' : 'bg-primary'
                  }`}
                  style={{ width: `${Math.min((usage.used / usage.limit) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {isPremium 
                  ? (isSpanish ? `${usage.used} de 7 reflexiones diarias usadas` : `${usage.used} of 7 daily reflections used`)
                  : (isSpanish ? `${usage.used} de 7 reflexiones diarias usadas` : `${usage.used} of 7 daily reflections used`)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {usage.next_reset_display 
                  ? (isSpanish ? `Se reinicia: ${usage.next_reset_display}` : `Resets: ${usage.next_reset_display}`)
                  : (isSpanish ? 'Se reinicia a medianoche en tu zona horaria' : 'Resets at midnight in your timezone')}
              </p>
            </div>
          )}
          
          {isPremium && user?.subscription_end_date && (
            <p className="text-sm text-muted-foreground mb-4">
              <Icon name="Calendar" size={14} className="inline mr-1" />
              {t('renews')}: {new Date(user.subscription_end_date).toLocaleDateString()}
            </p>
          )}
          {!isPremium && (
            <Button
              variant="default"
              onClick={handleUpgradeNow}
              disabled={upgradeLoading}
              className="gold-glow"
            >
              {upgradeLoading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  {isSpanish ? 'Cargando...' : 'Loading...'}
                </span>
              ) : (
                <>
                  <Icon name="Zap" size={18} className="mr-2" />
                  {isSpanish ? 'Mejorar Ahora' : 'Upgrade Now'}
                </>
              )}
            </Button>
          )}
          
          {/* Manage Subscription Button - for premium and trialing users */}
          {(isPremium || isTrialing) && (
            <Button
              variant="outline"
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="mt-4"
              data-testid="manage-subscription-btn"
            >
              <Icon name="Settings" size={18} className="mr-2" />
              {portalLoading 
                ? (isSpanish ? 'Cargando...' : 'Loading...') 
                : (isSpanish ? 'Administrar Suscripción' : 'Manage Subscription')}
            </Button>
          )}
        </div>

        {isPremium && (
          <div className="bento-item">
            <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
              {isSpanish ? 'Beneficios Premium' : 'Premium Benefits'}
            </h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="Check" size={16} color="#10B981" />
                {isSpanish ? '7 reflexiones diarias' : '7 daily reflections'}
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="Check" size={16} color="#10B981" />
                {isSpanish ? 'Notas de Campo y Diario Extendidos' : 'Extended Field Notes & Journaling'}
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="Check" size={16} color="#10B981" />
                {isSpanish ? 'Audio texto a voz' : 'Text-to-speech audio'}
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="Check" size={16} color="#10B981" />
                {isSpanish ? 'Soporte prioritario' : 'Priority support'}
              </li>
            </ul>
          </div>
        )}
        
        {isTrialing && (
          <div className="bento-item">
            <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
              {isSpanish ? 'Tu Plan Actual' : 'Your Current Plan'}
            </h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="Check" size={16} color="#10B981" />
                {isSpanish ? '7 reflexiones diarias' : '7 daily reflections'}
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="Check" size={16} color="#10B981" />
                {isSpanish ? 'Notas de Campo y Diario' : 'Field Notes & Journaling'}
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="Check" size={16} color="#10B981" />
                {isSpanish ? 'Audio texto a voz' : 'Text-to-speech audio'}
              </li>
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderDataTab = () => (
    <div className="space-y-6">
      <div className="bento-item">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Shield" size={20} color="#10B981" />
          {t('dataPrivacy')}
        </h3>
        <div className="space-y-4 text-sm text-muted-foreground">
          <p>
            {isSpanish 
              ? 'Tus reflexiones y datos personales están encriptados y almacenados de forma segura. Nunca compartimos tu información con terceros.'
              : 'Your reflections and personal data are encrypted and stored securely. We never share your information with third parties.'}
          </p>
          <ul className="space-y-2">
            <li className="flex items-center gap-2">
              <Icon name="Lock" size={14} color="#10B981" />
              {isSpanish ? 'Encriptación de extremo a extremo' : 'End-to-end encryption'}
            </li>
            <li className="flex items-center gap-2">
              <Icon name="Shield" size={14} color="#10B981" />
              {isSpanish ? 'Cumplimiento GDPR' : 'GDPR compliant'}
            </li>
            <li className="flex items-center gap-2">
              <Icon name="Eye" size={14} color="#10B981" />
              {isSpanish ? 'No se venden datos a terceros' : 'No data sold to third parties'}
            </li>
          </ul>
        </div>
      </div>

      {/* Daily Reminders Toggle - DISABLED to prevent VAPID error
      <div className="bento-item">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Bell" size={20} color="#FFD700" />
          {t('dailyReminders')}
        </h3>
        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
          <div className="flex-1">
            <p className="font-medium text-foreground">
              {isSpanish ? 'Recibir recordatorios diarios de reflexión' : 'Receive daily reflection reminders'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {isSpanish 
                ? 'Recibe una notificación suave para tomar un momento de reflexión espiritual'
                : 'Get a gentle notification to take a moment for spiritual reflection'}
            </p>
            {!pushSupported && (
              <p className="text-xs text-warning mt-2">
                <Icon name="AlertCircle" size={12} className="inline mr-1" />
                {t('notificationsNotSupported')}
              </p>
            )}
            {pushSupported && getNotificationPermission() === 'denied' && (
              <p className="text-xs text-error mt-2">
                <Icon name="AlertCircle" size={12} className="inline mr-1" />
                {isSpanish 
                  ? 'Notificaciones bloqueadas. Por favor habilita en configuración del navegador.'
                  : 'Notifications blocked. Please enable in browser settings.'}
              </p>
            )}
          </div>
          <button
            onClick={handleNotificationToggle}
            disabled={notificationLoading || !pushSupported}
            data-testid="daily-reminders-toggle"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              notificationsEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                notificationsEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        {notificationLoading && (
          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
            <Icon name="Loader2" size={14} className="animate-spin" />
            {isSpanish ? 'Actualizando configuración de notificaciones...' : 'Updating notification settings...'}
          </p>
        )}
      </div>
      END Daily Reminders Toggle */}

      <div className="bento-item border-error/30">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Trash2" size={20} color="#EF4444" />
          {t('deleteAccount')}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {isSpanish 
            ? 'Eliminar permanentemente tu cuenta y todos los datos asociados. Esta acción no se puede deshacer.'
            : 'Permanently delete your account and all associated data. This action cannot be undone.'}
        </p>
        
        {!showDeleteConfirm ? (
          <Button
            variant="destructive"
            onClick={() => setShowDeleteConfirm(true)}
          >
            {isSpanish ? 'Eliminar Mi Cuenta y Datos' : 'Delete My Account & Data'}
          </Button>
        ) : (
          <div className="p-4 bg-error/10 rounded-lg border border-error/20">
            <p className="font-medium text-error mb-3">
              {isSpanish ? '¿Estás seguro? Esto eliminará permanentemente:' : 'Are you sure? This will permanently delete:'}
            </p>
            <ul className="text-sm text-muted-foreground mb-4 space-y-1">
              <li>• {isSpanish ? 'Tu cuenta y perfil' : 'Your account and profile'}</li>
              <li>• {isSpanish ? 'Todo el historial de reflexiones' : 'All reflection history'}</li>
              <li>• {isSpanish ? 'Notas de campo y diarios guardados' : 'Saved field notes and journals'}</li>
              <li>• {isSpanish ? 'Datos de suscripción' : 'Subscription data'}</li>
            </ul>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
              >
                {t('cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={loading}
              >
                {loading ? (isSpanish ? 'Eliminando...' : 'Deleting...') : (isSpanish ? 'Sí, Eliminar Todo' : 'Yes, Delete Everything')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile': return renderProfileTab();
      case 'security': return renderSecurityTab();
      case 'subscription': return renderSubscriptionTab();
      case 'data': return renderDataTab();
      default: return renderProfileTab();
    }
  };

  return (
    <>
      <Helmet>
        <title>{isSpanish ? 'Configuración de Cuenta - Lumiveta' : 'Account Settings - Lumiveta'}</title>
      </Helmet>

      <div className="min-h-dvh bg-background">
        <Header />

        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="mb-8">
                <h1 className="font-heading font-bold text-3xl text-gold-gradient mb-2">
                  {isSpanish ? 'Configuración de Cuenta' : 'Account Settings'}
                </h1>
                <p className="text-muted-foreground">
                  {isSpanish ? 'Administra tu perfil, seguridad y preferencias' : 'Manage your profile, security, and preferences'}
                </p>
              </div>

              {/* Message */}
              {message.text && (
                <div className={`mb-6 p-4 rounded-lg ${
                  message.type === 'success' 
                    ? 'bg-success/10 border border-success/20 text-success' 
                    : 'bg-error/10 border border-error/20 text-error'
                }`}>
                  {message.text}
                </div>
              )}

              {/* Layout */}
              <div className="grid lg:grid-cols-4 gap-6">
                {/* Sidebar */}
                <div className="lg:col-span-1">
                  <div className="glass rounded-xl p-4 sticky top-24">
                    <nav className="space-y-1">
                      {tabs.map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                            activeTab === tab.id
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }`}
                        >
                          <Icon name={tab.icon} size={18} />
                          <span className="text-sm font-medium">{tab.label}</span>
                        </button>
                      ))}
                    </nav>

                    {/* Admin Link */}
                    {user?.is_admin && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <button
                          onClick={() => navigate('/admin')}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-primary hover:bg-primary/10 transition-all"
                        >
                          <Icon name="Shield" size={18} />
                          <span className="text-sm font-medium">Admin Panel</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="lg:col-span-3">
                  {renderTabContent()}
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-border py-6">
          <div className="container mx-auto px-4">
            <p className="text-center text-sm text-muted-foreground">
              © 2026 GO OUTDOORS LLC™ | LEVERAGING AI FOR THE REST OF US™ | LUMIVETA™
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AccountSettings;
